import {
  ArrayVariableProperties,
  CustomStructureProperties,
  DateVariableProperties,
  EntryProperties,
  ObjectVariableProperties,
  VariableProperties,
} from '../domain/datatype/VariablePropertiesDomains';
import { DataStructure } from '../domain/structure/DataStructureDomains';
import DataType from '../constant/DataType';
import { notUndefined } from '../util/Assert';
import StructClient from '../client/struct/StructClient';

class VariablePropertiesJsonSerializer {
  private readonly structClient: StructClient;

  private readonly structCache: Record<string, DataStructure> = {};

  constructor(structClient: StructClient) {
    notUndefined(structClient, 'Struct client cannot be undefined cannot be undefined');
    this.structClient = structClient;
  }

  public async deserialize(json: VariableProperties, tenant: string = '', dataStructure?: string, structurePath?: Array<string>): Promise<VariableProperties> {
    await this.prepareStructs(json);

    switch (json.dataType) {
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
      case DataType.DATE:
        return this.deserializeBasicVariableProperties(json, tenant, dataStructure, structurePath);
      case DataType.ARRAY:
        return this.deserializeArrayVariableProperties(json as ArrayVariableProperties as ArrayVariableProperties, tenant, dataStructure, structurePath);
      case DataType.OBJECT:
        return this.deserializeObjectVariableProperties(json as ObjectVariableProperties, tenant, dataStructure, structurePath);
      case DataType.STRUCTURE:
        return this.deserializeStructureVariableProperties(json as CustomStructureProperties, tenant, structurePath);
      default:
        throw new Error('Unsupported data type');
    }
  }

  private async deserializeBasicVariableProperties(
    properties: VariableProperties,
    tenant: string,
    dataStructure?: string,
    structurePath?: Array<string>,
  ): Promise<VariableProperties> {
    const dataType: DataType = properties.dataType;
    switch (dataType) {
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
      case DataType.DATE:
        return {
          ...properties,
          dataStructure: structurePath?.join('.'),
        };
      default:
        throw new Error('Unsupported data type ' + dataType);
    }
  }

  private async deserializeObjectVariableProperties(
    properties: ObjectVariableProperties,
    tenant: string,
    dataStructure?: string,
    structurePath?: Array<string>,
  ): Promise<ObjectVariableProperties> {
    const variables: Array<EntryProperties> = await Promise.all(
      properties.variables.map(async (variable: EntryProperties) => {
        variable.properties = await this.deserialize(variable.properties, tenant, dataStructure, structurePath);
        return variable;
      }),
    );
    return { dataType: DataType.OBJECT, dataStructure: structurePath?.join('.'), variables };
  }

  private async deserializeArrayVariableProperties(
    properties: ArrayVariableProperties,
    tenant: string,
    dataStructure?: string,
    structurePath?: Array<string>,
  ): Promise<ArrayVariableProperties> {
    const dataType: DataType = properties.properties.dataType;
    switch (dataType) {
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
      case DataType.DATE:
        return {
          dataType: DataType.ARRAY,
          properties: await this.deserializeBasicVariableProperties(properties.properties, tenant, dataStructure, structurePath),
          dataStructure: structurePath?.join('.'),
        } as ArrayVariableProperties;
      case DataType.ARRAY:
        if ((properties.properties as ArrayVariableProperties).dataType == DataType.ARRAY) {
          return {
            dataType: DataType.ARRAY,
            dataStructure: structurePath?.join('.'),
            properties: await this.deserializeArrayVariableProperties(properties.properties as ArrayVariableProperties, tenant, dataStructure, structurePath),
          } as ArrayVariableProperties;
        }
        return this.deserializeArrayVariableProperties(properties.properties as ArrayVariableProperties, tenant, dataStructure, structurePath);
      case DataType.OBJECT:
        return {
          dataType: DataType.ARRAY,
          dataStructure: structurePath?.join('.'),
          properties: await this.deserialize(properties.properties as ObjectVariableProperties, tenant, dataStructure, structurePath),
        } as ArrayVariableProperties;
      case DataType.STRUCTURE:
        return {
          dataType: DataType.ARRAY,
          properties: await this.deserializeStructureVariableProperties(properties.properties as CustomStructureProperties, tenant, structurePath),
          dataStructure: structurePath?.join('.'),
        } as ArrayVariableProperties;
      default:
        throw new Error('Unsupported data type');
    }
  }

  private async deserializeStructureVariableProperties(
    json: CustomStructureProperties,
    tenant: string,
    structurePath: Array<string> = [],
  ): Promise<VariableProperties> {
    const properties: DataStructure | undefined = this.structCache[json.name] ?? (await this.structClient.getById(json.name));
    notUndefined(properties, 'Data structure properties cannot be undefined ' + json.name);
    const path: Array<string> = [...structurePath];
    path.push(json.name);
    return this.deserialize(properties?.properties!, tenant, json.name, path);
  }

  public serialize(object: VariableProperties, tenant?: string): VariableProperties {
    switch (object.dataType) {
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
        return this.serializeBasicVariableProperties(object);
      case DataType.DATE:
        return this.serializeDateVariableProperties(object as DateVariableProperties);
      case DataType.STRUCTURE:
      case DataType.UNKNOWN:
        return object;
      case DataType.ARRAY:
        if (object.dataStructure != undefined) {
          return {
            dataType: DataType.ARRAY,
            properties: {
              dataType: DataType.STRUCTURE,
              name: object.dataStructure,
            } as CustomStructureProperties,
          } as ArrayVariableProperties;
        }
        return this.serializeArrayVariableProperties(object as ArrayVariableProperties, tenant);
      case DataType.OBJECT:
        if (object.dataStructure != undefined) {
          return {
            dataType: DataType.STRUCTURE,
            name: object.dataStructure,
          } as CustomStructureProperties;
        }
        return this.serializeObjectVariableProperties(object as ObjectVariableProperties, tenant);
      default:
        throw new Error('Unsupported data type');
    }
  }

  private serializeBasicVariableProperties(properties: VariableProperties): VariableProperties {
    return {
      dataType: properties.dataType,
    };
  }

  private serializeDateVariableProperties(properties: DateVariableProperties): DateVariableProperties {
    return {
      dataType: DataType.DATE,
      format: properties.format || 'epochMillis',
    };
  }

  private serializeArrayVariableProperties(properties: ArrayVariableProperties, tenant?: string): ArrayVariableProperties {
    const dataType: DataType = properties.properties.dataType;

    switch (dataType) {
      case DataType.NUMBER:
      case DataType.STRING:
      case DataType.BOOLEAN:
        return {
          properties: this.serializeBasicVariableProperties(properties.properties),
          dataType: properties.dataType,
        };
      case DataType.DATE:
        return {
          properties: this.serializeDateVariableProperties(properties.properties as DateVariableProperties),
          dataType: properties.dataType,
        };
      case DataType.ARRAY:
        return this.serializeArrayVariableProperties(properties.properties as ArrayVariableProperties, tenant);
      case DataType.OBJECT:
        return {
          properties: this.serializeObjectVariableProperties(properties.properties as ObjectVariableProperties, tenant),
          dataType: properties.dataType,
        };
      default:
        throw new Error('Unsupported data type');
    }
  }

  private serializeObjectVariableProperties(properties: ObjectVariableProperties, tenant?: string): ObjectVariableProperties {
    return {
      variables: properties.variables.map((variable: EntryProperties) => {
        return {
          name: variable.name,
          properties: this.serialize(variable.properties, tenant),
          description: '',
        };
      }),
      dataType: properties.dataType,
    };
  }

  private async prepareStructs(properties: VariableProperties) {
    const names: Array<string> = [];
    const iterate = (properties: VariableProperties) => {
      switch (properties.dataType) {
        case DataType.ARRAY:
          iterate((properties as ArrayVariableProperties).properties);
          break;
        case DataType.OBJECT:
          for (const entry of (properties as ObjectVariableProperties).variables) {
            iterate(entry.properties);
          }
          break;
        case DataType.STRUCTURE:
          names.push((properties as CustomStructureProperties).name);
          break;
      }
    };
    iterate(properties);
    const neededStructNames = names.filter((name: string) => !this.structCache.hasOwnProperty(name));
    if (neededStructNames.length == 0) return;
    const structs: DataStructure[] = await this.structClient.getByIds(neededStructNames);
    structs.forEach((s) => (this.structCache[s.name] = s));
  }
}

export default VariablePropertiesJsonSerializer;
